/**
 * Whole-app rrweb recorder.
 *
 * Records the user's session and uploads small event chunks to the backend.
 * The backend validates ownership and writes chunks to private object storage.
 */

import { AxiosError } from 'axios';
import { workspaceHeaders } from '@rudi/api/workspace';
import type { EnhancedError } from '@rudi/api/errors';
import type { EndpointResponse } from '@rudi/api/schema';
import { apiClient, getAccessToken } from '@/platform/lib/api-client';
import {
  afterScreenFrame,
  hasPendingScreenInteraction,
  subscribeScreenPending,
  whenNoScreenPending,
} from '@/platform/performance/screenCompletion';
import {
  isPracticeComputer,
  isReplaySuppressed,
  subscribeReplaySuppression,
  watchPracticeEnrollment,
} from './suppression';

type RrwebEvent = unknown;

// rrweb emits what it held while a screen opened at the latest this long after.
const MUTATION_RELEASE_TIMEOUT_MS = 500;

interface RrwebRecordConfig {
  emit: (event: RrwebEvent) => void;
  collectFonts?: boolean;
  maskAllInputs?: boolean;
  maskInputOptions?: Record<string, boolean>;
  blockClass?: string;
  ignoreClass?: string;
}

interface RrwebRecord {
  (config: RrwebRecordConfig): () => void;
  addCustomEvent: (tag: string, payload: unknown) => void;
  takeFullSnapshot?: (isCheckout?: boolean) => void;
}

interface ReplaySessionCreateInput {
  kind?: 'chat' | 'app' | 'qa_review' | 'dev_review';
  page_url?: string;
  user_agent?: string;
  chat_thread_id?: string;
  /** @deprecated Use chat_thread_id. */
  chat_session_id?: string;
  metadata?: Record<string, unknown>;
}

interface ReplaySessionResponse {
  id: string;
  status: string;
}

declare global {
  interface Window {
    __rrweb_session_id__?: string;
    __rrweb_kind__?: ReplaySessionCreateInput['kind'];
  }
}

const FLUSH_INTERVAL_MS = 5_000;
// Sizing and persisting the buffer wait for an idle moment, at most this long.
// Doing both on every rrweb event serialized the whole buffer hundreds of
// times per screen switch (ADR 0059).
const BOOKKEEPING_TIMEOUT_MS = 1_000;
const BOOKKEEPING_COALESCE_MS = 200;
const FLUSH_EVENT_THRESHOLD = 100;
// An upload that waited, for a screen or for the recovery write before it,
// goes in idle time within this long.
const FLUSH_IDLE_TIMEOUT_MS = 1_000;
const FLUSH_BYTES_THRESHOLD = 256 * 1024;
const MAX_BUFFERED_EVENTS = FLUSH_EVENT_THRESHOLD * 10;
// A flush uploads the events that fit in about this much JSON and leaves the
// rest for the next one, so a backlog built up behind a retry goes out in steps.
const CHUNK_TARGET_BYTES = 900 * 1024;
// A page's full snapshot is one event and the replay shows nothing without it,
// so an event larger than the target goes alone in a chunk of its own. The
// backend takes 16 MiB per request (cortex_replay_upload_max_bytes) and 64 MiB
// once decompressed (cortex_replay_chunk_max_decompressed_bytes); a snapshot,
// mostly markup and inlined CSS, gzips to a tenth of its size. Only an event
// past these limits is dropped.
const MAX_PLAIN_EVENT_BYTES = 15 * 1024 * 1024;
const MAX_GZIP_EVENT_BYTES = 60 * 1024 * 1024;
// Smaller chunks are not worth compressing.
const GZIP_MIN_BYTES = 64 * 1024;

/** Retries after the first failed upload of a chunk before the recorder gives up. */
export const MAX_CHUNK_RETRIES = 6;
const RETRY_BASE_DELAY_MS = 2_000;
const RETRY_MAX_DELAY_MS = 60_000;

/**
 * Public pages whose URL carries a secret: signing invitations and practice
 * setup codes. The recorder never starts there and drops events from them.
 */
export function isReplayExcludedPath(pathname: string): boolean {
  return pathname.startsWith('/sign/') || pathname === '/practice-setup';
}

const SESSION_STORAGE_KEY = 'rrweb:session_id';
const BUFFER_LOCAL_STORAGE_PREFIX = 'rrweb:buf:';
const KIND_OVERRIDE_KEY = 'rrweb:kind_override';

let activeRecorder: RrwebRecorder | null = null;
let startGeneration = 0;
let recordApi: RrwebRecord | null = null;
let mutationBuffers: ReadonlyArray<{ lock(): void; unlock(): void }> = [];
// Every recorder that is attached, including one a racing start left behind.
const liveRecorders = new Set<RrwebRecorder>();
// What the app asked for. A suppression that ends restarts recording with these options.
let wantedOptions: StartRecorderOptions | null = null;
let unwatchSuppression: (() => void) | null = null;
let unwatchEnrollment: (() => void) | null = null;
// A backend from before gzip uploads refuses them; the page then sends plain JSON.
let gzipRefused = false;

export interface StartRecorderOptions {
  kind?: ReplaySessionCreateInput['kind'];
  chatThreadId?: string;
  /** @deprecated Use chatThreadId. */
  chatSessionId?: string;
}

export function recordRrwebMarker(tag: string, payload: unknown): void {
  if (!recordApi) return;
  try {
    recordApi.addCustomEvent(tag, payload);
  } catch (err) {
    console.warn('[rrweb] marker failed', tag, err);
  }
}

export async function startRrwebRecorder(
  opts: StartRecorderOptions = {}
): Promise<RrwebRecorder | null> {
  if (isReplayExcludedPath(window.location.pathname)) return null;
  wantedOptions = opts;
  watchSuppression();
  if (isReplaySuppressed()) return null;
  if (activeRecorder) return activeRecorder;
  const generation = ++startGeneration;
  const canStart = () =>
    generation === startGeneration &&
    wantedOptions !== null &&
    !isReplaySuppressed() &&
    !isReplayExcludedPath(window.location.pathname);
  unwatchEnrollment?.();
  unwatchEnrollment = watchPracticeEnrollment(() => {
    wantedOptions = null;
    startGeneration += 1;
    discardLiveRecorders();
  });
  try {
    // A practice computer shows patient records, so it never records.
    if ((await isPracticeComputer()) || !canStart()) return null;
    const recorder = new RrwebRecorder(opts);
    if (!(await recorder.start(canStart))) return null;
    if (!canStart()) {
      recorder.discard();
      return null;
    }
    activeRecorder = recorder;
    return recorder;
  } catch (err) {
    console.warn('[rrweb] failed to start recorder', err);
    return null;
  }
}

export async function stopRrwebRecorder(): Promise<void> {
  startGeneration += 1;
  wantedOptions = null;
  if (!activeRecorder) return;
  const recorder = activeRecorder;
  activeRecorder = null;
  await recorder.stop();
}

function watchSuppression(): void {
  unwatchSuppression ??= subscribeReplaySuppression(suppressed => {
    if (suppressed) {
      startGeneration += 1;
      discardLiveRecorders();
      return;
    }
    if (!wantedOptions || activeRecorder) return;
    void startRrwebRecorder(wantedOptions);
  });
}

/** Stops every recorder at once and drops its unsent events. */
function discardLiveRecorders(): void {
  activeRecorder = null;
  for (const recorder of [...liveRecorders]) recorder.discard();
}

class RrwebRecorder {
  private opts: StartRecorderOptions;
  private sessionId: string | null = null;
  private buffer: RrwebEvent[] = [];
  private bufferBytes = 0;
  private nextSeq = 0;
  private totalEvents = 0;
  private startedAtMs = 0;
  private stopFn: (() => void) | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempts = 0;
  private flushing = false;
  private unloadHandler: (() => void) | null = null;
  private closeQueued = false;
  private discarded = false;
  private detached = false;
  private lastEventAt = 0;
  private bookkeeping: { cancel: () => void } | null = null;
  private deferredFlush: (() => void) | null = null;
  private idleFlush: { cancel: () => void } | null = null;
  private thresholdFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private hideHandler: (() => void) | null = null;
  private pageHideHandler: ((event: PageTransitionEvent) => void) | null = null;
  private unwatchScreenPending: (() => void) | null = null;
  private mutationsHeld = false;
  private pendingRelease: { cancel: () => void } | null = null;
  private needsCheckout = false;
  private cancelCheckout: (() => void) | null = null;

  constructor(opts: StartRecorderOptions) {
    this.opts = opts;
  }

  async start(canStart: () => boolean): Promise<boolean> {
    await this.recoverPreviousSession();
    if (!canStart()) return false;

    const record = await loadRecordApi();
    if (!canStart()) return false;
    recordApi = record;
    // Each session tries gzip again, in case the backend has been updated since.
    gzipRefused = false;

    const kind = normalizeReplayKind(
      this.opts.kind ?? window.__rrweb_kind__ ?? readKindOverride() ?? 'chat'
    );
    const created = await createReplaySession({
      kind,
      page_url: isReplayExcludedPath(window.location.pathname)
        ? `${window.location.origin}${window.location.pathname}`
        : window.location.href,
      user_agent: navigator.userAgent,
      chat_thread_id: this.opts.chatThreadId ?? this.opts.chatSessionId,
    });

    this.sessionId = created.session_id;
    this.startedAtMs = Date.now();
    // The session response may arrive during a new app opening after Home settled.
    if (hasPendingScreenInteraction()) {
      await new Promise<void>(resolve => afterScreenFrame(resolve));
    }
    if (!canStart()) {
      this.sessionId = null;
      await closeReplaySession(created.session_id, {
        duration_ms: Date.now() - this.startedAtMs,
        events_count: 0,
        chunk_count: 0,
      });
      return false;
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY, this.sessionId);
    window.__rrweb_session_id__ = this.sessionId;

    this.stopFn = record({
      collectFonts: true,
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: true,
        text: true,
        textarea: true,
        number: true,
        tel: true,
        url: true,
        search: true,
      },
      blockClass: 'rr-block',
      ignoreClass: 'rr-ignore',
      emit: event => this.onEvent(event),
    });
    liveRecorders.add(this);
    // Serializing a screen's new DOM while it opens delays its first frame.
    // rrweb keeps collecting the changes and emits them as one event after.
    this.holdMutations(hasPendingScreenInteraction());
    this.unwatchScreenPending = subscribeScreenPending(pending => {
      if (pending) this.holdMutations(true);
      else this.releaseMutationsWhenIdle();
    });

    this.flushTimer = setInterval(() => {
      void this.flush('interval');
    }, FLUSH_INTERVAL_MS);

    this.unloadHandler = () => {
      this.keepaliveClose();
    };
    window.addEventListener('beforeunload', this.unloadHandler);
    // A hidden tab may be discarded without unload; keep its buffer recoverable.
    this.hideHandler = () => {
      if (document.visibilityState === 'hidden') this.settleBuffer();
    };
    document.addEventListener('visibilitychange', this.hideHandler);
    // A page that is not kept in the back/forward cache is going away, so close
    // its session as beforeunload does: not every unload fires beforeunload, and
    // keeping a recovery copy of the buffer there only delays the next page.
    this.pageHideHandler = event => {
      if (event.persisted) this.settleBuffer();
      else this.keepaliveClose();
    };
    window.addEventListener('pagehide', this.pageHideHandler);
    return true;
  }

  async stop(): Promise<void> {
    this.detach();

    // A flush uploads at most one chunk, so drain until the buffer is empty or
    // an upload fails and leaves it as it was.
    while (this.buffer.length > 0) {
      const before = this.buffer.length;
      await this.flush('stop');
      if (this.buffer.length >= before) break;
    }

    if (this.sessionId) {
      try {
        await closeReplaySession(this.sessionId, {
          duration_ms: Date.now() - this.startedAtMs,
          events_count: this.totalEvents,
          chunk_count: this.nextSeq,
        });
      } catch (err) {
        console.warn('[rrweb] close failed', err);
      }
      this.clearPersistedBuffer(this.sessionId);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      window.__rrweb_session_id__ = undefined;
      this.sessionId = null;
    }
  }

  /**
   * Stops at once, without uploading what is buffered. Used while a screen with
   * patient data is open. The session is closed with the counts sent so far.
   */
  discard(): void {
    if (this.discarded) return;
    this.discarded = true;
    this.detach();
    this.buffer = [];
    this.bufferBytes = 0;
    const sessionId = this.sessionId;
    if (!sessionId) return;
    this.clearPersistedBuffer(sessionId);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
    window.__rrweb_session_id__ = undefined;
    this.sessionId = null;
    void closeReplaySession(sessionId, {
      duration_ms: Date.now() - this.startedAtMs,
      events_count: this.totalEvents,
      chunk_count: this.nextSeq,
    }).catch(err => {
      if (!isReplaySessionClosed(err)) console.warn('[rrweb] close failed', err);
    });
  }

  /**
   * Emitting what rrweb held costs ~30 ms for a List; doing it in the task
   * after the screen's frame delayed the next frame, so it waits for idle
   * time. A screen that starts waiting meanwhile keeps the hold.
   */
  private releaseMutationsWhenIdle(): void {
    if (!this.mutationsHeld || this.pendingRelease) return;
    const release = () => {
      this.pendingRelease = null;
      this.holdMutations(false);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(release, { timeout: MUTATION_RELEASE_TIMEOUT_MS });
      this.pendingRelease = { cancel: () => window.cancelIdleCallback(id) };
    } else {
      const timer = setTimeout(release, 0);
      this.pendingRelease = { cancel: () => clearTimeout(timer) };
    }
  }

  private holdMutations(hold: boolean): void {
    this.pendingRelease?.cancel();
    this.pendingRelease = null;
    if (hold === this.mutationsHeld) return;
    this.mutationsHeld = hold;
    for (const buffer of mutationBuffers) {
      if (hold) buffer.lock();
      else buffer.unlock();
    }
  }

  private detach(): void {
    this.detached = true;
    liveRecorders.delete(this);
    this.unwatchScreenPending?.();
    this.unwatchScreenPending = null;
    if (this.stopFn) {
      this.holdMutations(false);
      try {
        this.stopFn();
      } catch {
        // Ignore recorder teardown errors.
      }
      this.stopFn = null;
    }
    if (this.thresholdFlushTimer !== null) {
      clearTimeout(this.thresholdFlushTimer);
      this.thresholdFlushTimer = null;
    }
    this.deferredFlush?.();
    this.deferredFlush = null;
    this.idleFlush?.cancel();
    this.idleFlush = null;
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.unloadHandler) {
      window.removeEventListener('beforeunload', this.unloadHandler);
      this.unloadHandler = null;
    }
    if (this.hideHandler) {
      document.removeEventListener('visibilitychange', this.hideHandler);
      this.hideHandler = null;
    }
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    this.bookkeeping?.cancel();
    this.bookkeeping = null;
    this.cancelCheckout?.();
    this.cancelCheckout = null;
  }

  private onEvent(event: RrwebEvent): void {
    // Signing and setup URLs carry secrets and must never enter replay data.
    if (isReplayExcludedPath(window.location.pathname)) return;
    if (this.discarded || isReplaySuppressed()) return;
    this.lastEventAt = performance.now();
    this.buffer.push(event);
    this.totalEvents += 1;
    if (this.buffer.length > MAX_BUFFERED_EVENTS) {
      this.buffer.splice(0, this.buffer.length - MAX_BUFFERED_EVENTS);
      this.bufferBytes = estimateBytes(this.buffer);
      this.noteLoss(false);
    }
    if (this.buffer.length >= FLUSH_EVENT_THRESHOLD && this.thresholdFlushTimer === null) {
      // A captured click may start a screen in its later click handler.
      this.thresholdFlushTimer = setTimeout(() => {
        this.thresholdFlushTimer = null;
        void this.flush('threshold');
      }, 0);
    }
    this.scheduleBookkeeping();
  }

  private scheduleBookkeeping(): void {
    if (this.detached || this.bookkeeping) return;
    const deadline = performance.now() + BOOKKEEPING_TIMEOUT_MS;
    const run = () => {
      this.bookkeeping = null;
      // Panel and pagination mutations can continue after the enclosing screen
      // completed. Wait for their quiet period within the same recovery deadline.
      const remaining = deadline - performance.now();
      const pending = hasPendingScreenInteraction();
      const quietRemaining = BOOKKEEPING_COALESCE_MS - (performance.now() - this.lastEventAt);
      if (remaining > 0 && (pending || quietRemaining > 0)) {
        const delay = pending ? BOOKKEEPING_COALESCE_MS : quietRemaining;
        const timer = setTimeout(run, Math.max(1, Math.min(delay, remaining)));
        this.bookkeeping = { cancel: () => clearTimeout(timer) };
        return;
      }
      this.settleBuffer();
    };
    // Responses often arrive in separate idle periods during one navigation.
    // Coalesce their mutations before serializing the recovery buffer again.
    const timer = setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        const id = window.requestIdleCallback(run, {
          timeout: BOOKKEEPING_TIMEOUT_MS - BOOKKEEPING_COALESCE_MS,
        });
        this.bookkeeping = { cancel: () => window.cancelIdleCallback(id) };
      } else {
        run();
      }
    }, BOOKKEEPING_COALESCE_MS);
    this.bookkeeping = { cancel: () => clearTimeout(timer) };
  }

  /** Serialize the recovery buffer once and flush when large. */
  private settleBuffer(): void {
    // On a reload or a navigation away, beforeunload or pagehide has already
    // closed the session and cleared its recovery buffer, and visibilitychange
    // follows. Serializing the buffer there, which holds the full snapshot
    // until the first upload, blocked the next page for 50-110 ms and wrote
    // nothing.
    if (this.discarded || !this.sessionId || this.closeQueued) return;
    let serializedBuffer: string | undefined;
    try {
      serializedBuffer = JSON.stringify(this.buffer);
      this.bufferBytes = serializedBuffer.length;
    } catch {
      this.bufferBytes = this.buffer.length * 256;
    }
    this.persistBuffer(serializedBuffer);
    // Serializing the chunk in this task too made one task of 75-130 ms,
    // which landed in the next screen's opening.
    if (this.bufferBytes >= FLUSH_BYTES_THRESHOLD) this.flushWhenIdle('threshold');
  }

  private flushWhenIdle(reason: string): void {
    if (this.detached || this.idleFlush) return;
    this.idleFlush = whenIdle(() => {
      this.idleFlush = null;
      void this.flush(reason);
    }, FLUSH_IDLE_TIMEOUT_MS);
  }

  private async flush(reason: string): Promise<void> {
    if (this.flushing || !this.sessionId || this.buffer.length === 0) return;
    // While a retry is scheduled, only the retry timer or stop() may upload.
    if (this.retryTimer && reason !== 'stop') return;
    // An upload must not queue ahead of the data of a screen the user opened.
    if (reason !== 'stop' && hasPendingScreenInteraction()) {
      if (this.deferredFlush) return;
      // The screen's frame is presented by then, and the user may already act
      // on it; the upload waits for idle time as well.
      this.deferredFlush = whenNoScreenPending(() => {
        this.deferredFlush = null;
        this.flushWhenIdle(reason);
      });
      return;
    }

    this.flushing = true;
    const sessionId = this.sessionId;
    const seq = this.nextSeq;
    const { events, rest, dropped, droppedSnapshot, body } = takeChunk(this.buffer);
    if (dropped > 0) {
      console.warn(`[rrweb] dropped ${dropped} event(s) too large for a replay chunk`);
      this.noteLoss(droppedSnapshot);
    }
    this.buffer = rest;
    this.bufferBytes = estimateBytes(rest);
    if (events.length === 0) {
      this.persistBuffer();
      this.flushing = false;
      return;
    }

    try {
      const result = await sendChunk(sessionId, seq, body ?? JSON.stringify(events));
      if (this.discarded) return;
      this.retryAttempts = 0;
      if (!result.accepted) {
        this.handleClosedSession();
        return;
      }
      this.nextSeq += 1;
      if (this.needsCheckout) this.scheduleCheckout();
      // Acknowledging an earlier upload must not serialize a panel's new DOM
      // mutations while its data and presentation are still settling.
      if (
        reason !== 'stop' &&
        (hasPendingScreenInteraction() ||
          performance.now() - this.lastEventAt < BOOKKEEPING_COALESCE_MS)
      )
        this.scheduleBookkeeping();
      else this.persistBuffer();
    } catch (err) {
      // A discarded recorder keeps nothing, not even a chunk that failed.
      if (this.discarded) return;
      const failure = classifyChunkFailure(err);
      if (failure.kind === 'closed') {
        this.handleClosedSession();
        return;
      }
      if (failure.kind === 'too_large') {
        // The same bytes would be refused again, so they are not retried.
        console.warn('[rrweb] replay chunk refused as too large; dropping it', reason, err);
        this.retryAttempts = 0;
        this.noteLoss(events.some(isFullSnapshot));
        this.persistBuffer();
        return;
      }
      if (failure.kind === 'unauthorized') {
        // The API client has already tried one token refresh for this request.
        console.warn('[rrweb] chunk upload not authorized; stopping recorder', reason, err);
        this.handleClosedSession();
        this.halt();
        return;
      }

      console.warn('[rrweb] chunk flush failed', reason, err);
      this.requeue(events);
      if (reason === 'stop') return;

      this.retryAttempts += 1;
      if (this.retryAttempts > MAX_CHUNK_RETRIES) {
        // Keep the buffer and session id so the next page load uploads and closes it.
        console.warn('[rrweb] chunk upload keeps failing; stopping recorder');
        this.persistBuffer();
        this.sessionId = null;
        window.__rrweb_session_id__ = undefined;
        this.buffer = [];
        this.bufferBytes = 0;
        this.halt();
        return;
      }
      this.scheduleRetry(retryDelayMs(this.retryAttempts, failure.retryAfterMs));
    } finally {
      this.flushing = false;
    }
  }

  private requeue(events: RrwebEvent[]): void {
    const overflow = events.length + this.buffer.length - MAX_BUFFERED_EVENTS;
    if (overflow > 0) {
      events.splice(0, overflow);
      this.noteLoss(false);
    }
    this.buffer = events.concat(this.buffer);
    this.bufferBytes = estimateBytes(this.buffer);
  }

  /**
   * Events that never reach the backend leave the replay out of step with the
   * page from then on. The next upload that succeeds is followed by a new full
   * snapshot, which puts it right. A full snapshot too large to send would be
   * as large again, so losing one for its size does not ask for another.
   */
  private noteLoss(lostSnapshotForSize: boolean): void {
    if (!lostSnapshotForSize) this.needsCheckout = true;
  }

  private scheduleCheckout(): void {
    this.needsCheckout = false;
    if (this.cancelCheckout || this.detached) return;
    // Outside rrweb's emit, and not while a screen opens: a snapshot serializes the whole page.
    let cancelWait: (() => void) | null = null;
    const timer = setTimeout(() => {
      cancelWait = whenNoScreenPending(() => {
        this.cancelCheckout = null;
        if (this.detached || !recordApi?.takeFullSnapshot) return;
        try {
          recordApi.takeFullSnapshot(true);
        } catch (err) {
          console.warn('[rrweb] new full snapshot failed', err);
        }
        // Taking a snapshot unlocks rrweb's mutation buffers.
        if (this.mutationsHeld) for (const buffer of mutationBuffers) buffer.lock();
      });
    }, 0);
    this.cancelCheckout = () => {
      clearTimeout(timer);
      cancelWait?.();
    };
  }

  private scheduleRetry(delayMs: number): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush('retry');
    }, delayMs);
  }

  /** Stop recording for good. A later startRrwebRecorder() call starts a new session. */
  private halt(): void {
    this.detach();
    if (activeRecorder === this) {
      activeRecorder = null;
    }
  }

  private handleClosedSession(): void {
    if (this.sessionId) {
      this.clearPersistedBuffer(this.sessionId);
    }
    this.buffer = [];
    this.bufferBytes = 0;
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
    window.__rrweb_session_id__ = undefined;
    this.sessionId = null;
  }

  private keepaliveClose(): void {
    if (!this.sessionId || this.closeQueued) return;
    const token = getAccessToken();
    if (!token) return;

    try {
      const sessionId = this.sessionId;
      void fetch(apiUrl(`/cortex/replays/${sessionId}/close`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...workspaceHeaders(),
        },
        body: JSON.stringify({
          duration_ms: Date.now() - this.startedAtMs,
          events_count: this.totalEvents,
          chunk_count: this.nextSeq,
        }),
        keepalive: true,
      });
      this.closeQueued = true;
      this.clearPersistedBuffer(sessionId);
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      window.__rrweb_session_id__ = undefined;
      this.sessionId = null;
    } catch {
      // Page is unloading; nothing useful can be done here.
    }
  }

  private persistBuffer(serializedBuffer?: string): void {
    if (!this.sessionId) return;
    try {
      localStorage.setItem(
        `${BUFFER_LOCAL_STORAGE_PREFIX}${this.sessionId}`,
        serializedBuffer === undefined
          ? JSON.stringify({
              buffer: this.buffer,
              nextSeq: this.nextSeq,
              totalEvents: this.totalEvents,
            })
          : `{"buffer":${serializedBuffer},"nextSeq":${this.nextSeq},"totalEvents":${this.totalEvents}}`
      );
    } catch {
      // localStorage full or denied; recording can continue without recovery.
    }
  }

  private clearPersistedBuffer(sessionId: string): void {
    try {
      localStorage.removeItem(`${BUFFER_LOCAL_STORAGE_PREFIX}${sessionId}`);
    } catch {
      // Ignore storage errors.
    }
  }

  private async recoverPreviousSession(): Promise<void> {
    let staleId: string | null;
    try {
      staleId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    } catch {
      return;
    }
    if (!staleId) return;

    let raw: string | null;
    try {
      raw = localStorage.getItem(`${BUFFER_LOCAL_STORAGE_PREFIX}${staleId}`);
    } catch {
      raw = null;
    }
    let leftover: { buffer?: RrwebEvent[]; nextSeq?: number; totalEvents?: number } = {};
    if (raw) {
      try {
        leftover = JSON.parse(raw) ?? {};
      } catch {
        leftover = {};
      }
    }

    let seq = leftover.nextSeq ?? 0;
    let buffer = leftover.buffer ?? [];
    const totalEvents = leftover.totalEvents ?? buffer.length;

    while (buffer.length > 0) {
      const { events, rest, body } = takeChunk(buffer);
      buffer = rest;
      if (events.length === 0) continue;
      try {
        const result = await sendChunk(staleId, seq, body ?? JSON.stringify(events));
        if (!result.accepted) break;
        seq += 1;
      } catch (err) {
        if (!isReplaySessionClosed(err)) {
          console.warn('[rrweb] recovery flush failed', err);
        }
        break;
      }
    }

    try {
      await closeReplaySession(staleId, {
        events_count: totalEvents,
        chunk_count: seq,
      });
    } catch (err) {
      if (!isReplaySessionClosed(err)) {
        console.warn('[rrweb] recovery close failed', err);
      }
    }

    this.clearPersistedBuffer(staleId);
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }
}

async function loadRecordApi(): Promise<RrwebRecord> {
  const [mod, observer] = await Promise.all([
    import('rrweb') as Promise<{ record: RrwebRecord }>,
    import('rrweb/es/rrweb/packages/rrweb/src/record/observer.js'),
  ]);
  mutationBuffers = observer.mutationBuffers;
  return mod.record;
}

async function createReplaySession(
  input: ReplaySessionCreateInput
): Promise<{ session_id: string }> {
  const res = await apiClient.post('/cortex/replays/sessions', input);
  return res.data as { session_id: string };
}

type ChunkUploadResult = { accepted: boolean; seq: number; storage_key?: string };

/**
 * Uploads one chunk, gzipped when it is large enough to gain from it. A backend
 * from before gzip uploads refuses the gzip body with 400 or 415, so the chunk
 * goes again as plain JSON and the page stops compressing.
 */
async function sendChunk(sessionId: string, seq: number, json: string): Promise<ChunkUploadResult> {
  if (canGzip() && json.length >= GZIP_MIN_BYTES) {
    let compressed: Uint8Array | null = null;
    try {
      compressed = await gzip(json);
    } catch (err) {
      console.warn('[rrweb] gzip failed; uploading plain JSON', err);
    }
    if (compressed) {
      try {
        return await uploadReplayChunk(sessionId, seq, compressed, true);
      } catch (err) {
        if (!refusesGzip(err)) throw err;
        gzipRefused = true;
      }
    }
  }
  return uploadReplayChunk(sessionId, seq, json, false);
}

async function uploadReplayChunk(
  sessionId: string,
  seq: number,
  body: string | Uint8Array,
  gzipped: boolean
): Promise<ChunkUploadResult> {
  const res = await apiClient.post<EndpointResponse<'/cortex/replays/{session_id}/chunks', 'post'>>(
    `/cortex/replays/${sessionId}/chunks`,
    body,
    {
      params: { seq },
      headers: {
        'Content-Type': 'application/json',
        ...(gzipped ? { 'Content-Encoding': 'gzip' } : {}),
      },
      transformRequest: data => data,
    }
  );
  return res.data as ChunkUploadResult;
}

function canGzip(): boolean {
  return !gzipRefused && typeof CompressionStream === 'function';
}

async function gzip(json: string): Promise<Uint8Array> {
  const bytes = utf8.encode(json);
  const stream = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  }).pipeThrough(new CompressionStream('gzip'));
  const reader = stream.getReader();
  const parts: Uint8Array[] = [];
  let length = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    length += value.length;
  }
  const out = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function refusesGzip(err: unknown): boolean {
  const status = err instanceof AxiosError ? err.response?.status : undefined;
  return status === 400 || status === 415;
}

async function closeReplaySession(
  sessionId: string,
  body: { duration_ms?: number; events_count: number; chunk_count: number }
): Promise<ReplaySessionResponse> {
  const res = await apiClient.post(`/cortex/replays/${sessionId}/close`, body);
  return res.data as ReplaySessionResponse;
}

function apiUrl(path: string): string {
  const base = apiClient.defaults.baseURL || '/api';
  if (/^https?:\/\//.test(base)) {
    return new URL(path.replace(/^\//, ''), `${base.replace(/\/$/, '')}/`).toString();
  }
  return `${base.replace(/\/$/, '')}${path}`;
}

type ChunkFailure =
  | { kind: 'closed' }
  | { kind: 'unauthorized' }
  | { kind: 'too_large' }
  | { kind: 'retry'; retryAfterMs?: number };

function classifyChunkFailure(err: unknown): ChunkFailure {
  if (isReplaySessionClosed(err)) return { kind: 'closed' };
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    if (status === 401 || status === 403 || status === 440) return { kind: 'unauthorized' };
    if (status === 413) return { kind: 'too_large' };
    // Network errors, timeouts, 408, 5xx and other 4xx get a bounded number of retries.
    return { kind: 'retry' };
  }
  const enhanced = err as EnhancedError | null;
  if (enhanced?.status === 429) {
    return { kind: 'retry', retryAfterMs: parseRetryAfterMs(enhanced.retryAfter) };
  }
  // Anything else did not come back as an HTTP response to this request. The API
  // client's 401 handler rejects with a plain Error when the token refresh fails.
  return { kind: 'unauthorized' };
}

function retryDelayMs(attempt: number, retryAfterMs?: number): number {
  if (retryAfterMs !== undefined) {
    return Math.min(RETRY_MAX_DELAY_MS, Math.max(RETRY_BASE_DELAY_MS, retryAfterMs));
  }
  const ceiling = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
  // Jitter spreads out tabs that all failed at the same moment, e.g. during a backend restart.
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function parseRetryAfterMs(value: number | string | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(String(value));
  return Number.isNaN(date) ? undefined : Math.max(0, date - Date.now());
}

const utf8 = new TextEncoder();

/** The UTF-8 size of a string, without encoding a copy of it as TextEncoder does. */
export function utf8ByteLength(text: string): number {
  let bytes = text.length;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x80) continue;
    if (code < 0x800) bytes += 1;
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < text.length) {
      const next = text.charCodeAt(index + 1);
      // A surrogate pair is four bytes for its two code units.
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 2;
        index += 1;
      } else bytes += 2;
    } else bytes += 2;
  }
  return bytes;
}

function whenIdle(run: () => void, timeout: number): { cancel: () => void } {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout });
    return { cancel: () => window.cancelIdleCallback(id) };
  }
  const timer = setTimeout(run, 0);
  return { cancel: () => clearTimeout(timer) };
}

/**
 * Split off the leading events whose JSON array fits in one chunk. An event
 * larger than a chunk goes alone, and only one past the upload limit is dropped.
 */
function takeChunk(buffer: RrwebEvent[]): {
  events: RrwebEvent[];
  rest: RrwebEvent[];
  dropped: number;
  droppedSnapshot: boolean;
  body: string | undefined;
} {
  const maxEventBytes = canGzip() ? MAX_GZIP_EVENT_BYTES : MAX_PLAIN_EVENT_BYTES;
  const events: RrwebEvent[] = [];
  const encoded: string[] = [];
  let complete = true;
  let dropped = 0;
  let droppedSnapshot = false;
  let bytes = 2; // the array brackets
  let index = 0;
  for (; index < buffer.length; index += 1) {
    const event = buffer[index];
    let size: number;
    let json: string | undefined;
    let failed = false;
    try {
      json = JSON.stringify(event) ?? 'null';
      size = utf8ByteLength(json) + 1;
    } catch {
      size = 256;
      failed = true;
    }
    if (size + 2 > maxEventBytes) {
      dropped += 1;
      droppedSnapshot ||= isFullSnapshot(event);
      continue;
    }
    if (events.length > 0 && bytes + size > CHUNK_TARGET_BYTES) break;
    events.push(event);
    encoded.push(json ?? 'null');
    complete &&= !failed;
    bytes += size;
  }
  return {
    events,
    rest: buffer.slice(index),
    dropped,
    droppedSnapshot,
    body: complete ? `[${encoded.join(',')}]` : undefined,
  };
}

function isFullSnapshot(event: RrwebEvent): boolean {
  return (event as { type?: unknown } | null)?.type === 2;
}

function estimateBytes(events: RrwebEvent[]): number {
  try {
    return JSON.stringify(events).length;
  } catch {
    return events.length * 256;
  }
}

function isReplaySessionClosed(err: unknown): boolean {
  return (
    err instanceof AxiosError &&
    (err.response?.status === 404 || err.response?.status === 409) &&
    typeof err.response.data?.detail === 'string' &&
    err.response.data.detail.includes('Replay session')
  );
}

function readKindOverride(): ReplaySessionCreateInput['kind'] | undefined {
  try {
    const value = localStorage.getItem(KIND_OVERRIDE_KEY);
    if (!value) return undefined;
    localStorage.removeItem(KIND_OVERRIDE_KEY);
    if (value === 'qa_review' || value === 'dev_review' || value === 'chat' || value === 'app') {
      return value;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function normalizeReplayKind(
  kind: ReplaySessionCreateInput['kind']
): ReplaySessionCreateInput['kind'] {
  return kind === 'app' ? 'chat' : kind;
}
